import { getMessageKind } from "components/ChatWidget/chatSupport.helpers";
import { HOSPITAL_STAFF_BOT_NAME } from "components/ChatWidget/hospitalStaffBot.constants";
import { ChatSupportStatus, IChat, IChatMessage } from "types/Chat";

/**
 * CLE-3043 — the admin Chatbot page's support rules (Chats tab and the chat view), pure so they are
 * tested directly.
 */

/** The chat view re-reads the chat this often while support is involved. */
export const ADMIN_SUPPORT_POLL_INTERVAL_MS = 3000;

export const supportStatusOf = (
  chat: Pick<IChat, "supportStatus"> | null | undefined
): ChatSupportStatus => chat?.supportStatus ?? "NONE";

/** RTK Query `pollingInterval`: 3 s while WAITING or HUMAN, otherwise off (0). */
export const getAdminPollingInterval = (
  supportStatus: ChatSupportStatus | null | undefined
): number =>
  supportStatus === "WAITING" || supportStatus === "HUMAN"
    ? ADMIN_SUPPORT_POLL_INTERVAL_MS
    : 0;

/**
 * Take over: from the queue (WAITING) only. The API refuses a NONE chat (409), because the staff
 * widget polls for support's lines only while WAITING / HUMAN — a chat taken from the bot would not
 * show them until the staff member sent another message.
 */
export const canTakeOver = (supportStatus: ChatSupportStatus): boolean =>
  supportStatus === "WAITING";

/** End chat: only once support is involved. End-only — there is no hand-back to the bot. */
export const canEndChat = (supportStatus: ChatSupportStatus): boolean =>
  supportStatus === "WAITING" || supportStatus === "HUMAN";

/** The reply box: only while support has the chat. */
export const canReply = (supportStatus: ChatSupportStatus): boolean =>
  supportStatus === "HUMAN";

export const SUPPORT_STATUS_LABELS: Record<ChatSupportStatus, string> = {
  NONE: "Bot",
  WAITING: "Waiting for support",
  HUMAN: "Support in chat",
  CLOSED: "Closed",
};

/** PrimeReact Tag severities. */
export const SUPPORT_STATUS_SEVERITY: Record<
  ChatSupportStatus,
  "info" | "warning" | "success" | "danger" | undefined
> = {
  NONE: "info",
  WAITING: "warning",
  HUMAN: "success",
  CLOSED: undefined,
};

/** The staff member who started the chat, as the API gives them; null when it does not. */
export const getStaffUserName = (
  chat: Pick<IChat, "user"> | null | undefined
): string | null => {
  const user = chat?.user;
  if (!user) return null;
  const name = typeof user.name === "string" ? user.name.trim() : "";
  return name || user.email || null;
};

/**
 * Whether a URL may be rendered as a link: absolute http(s) only. `escalationPageUrl` is reported by
 * the staff member's browser, so anything else (`javascript:`, `data:`, relative) is shown as text.
 */
export const isSafeHttpUrl = (url: string | null | undefined): url is string =>
  typeof url === "string" && /^https?:\/\//i.test(url);

/**
 * Who a transcript line is from. Patient chats keep their historical "Patient" / "Clara". Staff
 * (ContractManagementBot) chats: the staff member's name (fallback "Hospital staff"), the bot's name,
 * the support person's `authorName`; a system line has no sender (null) and renders centred.
 */
export const getTranscriptSenderLabel = (
  message: Pick<IChatMessage, "role" | "author" | "authorName">,
  chatType: string | null | undefined,
  staffName?: string | null
): string | null => {
  if (chatType !== "ContractManagementBot") {
    return message.role === "user" ? "Patient" : "Clara";
  }
  switch (getMessageKind(message)) {
    case "system":
      return null;
    case "support":
      return message.authorName || "Clear Health support";
    case "user":
      return staffName || "Hospital staff";
    default:
      return HOSPITAL_STAFF_BOT_NAME;
  }
};

const time = (value: string | null | undefined): number => {
  const ms = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
};

/**
 * The Chats tab's "Waiting for support" quick filter: the server returns the WAITING chats
 * (`GET /chats/all?supportStatus=WAITING`); this puts the one waiting longest on top (oldest
 * escalation first; a chat with no escalation time last). Returns a new array.
 */
export const sortByLongestWaiting = <T extends { escalatedAt?: string | null }>(
  rows: T[]
): T[] => [...rows].sort((a, b) => time(a.escalatedAt) - time(b.escalatedAt));
